import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import trans from 'counterpart';
import { Link } from 'react-router-dom';
import throttle from 'lodash.throttle';
import { Popover } from 'reactstrap';
import { getPreferences } from '../../preferences/selectors';
import Filters from './Filters';
import { getPreference, hasAdults } from '../../../selectors/preferenceSelectors';
import { PREFERENCE_SUBJECTS, PREFERENCE_ARRIVAL } from '../../../constants/preferences';
import { getBookingUrl } from '../../../utilities/preferenceUtilities';
import { fetchOffers, setArrivalPreference, fetchArrivalsAndDepartures } from '../../../actions/preferenceActions';

class AccommodationInfoBlock extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFetching: false,
      popoverOpen: false,
      popoverOpenAllInDesktop: false,
      popoverOpenAllInMobile: false,
      error: null,
      results: [],
      stickyGallery: false,
      stickyGalleryOpen: false
    };
    this.throttled = throttle(this.scrollEventHandler, 150, { leading: true });
  }

  componentDidMount = () => {
    this.fetchResults()
      .then((data) => {
        if (data && !data.results.length) {
          const closestArrival = [...data.arrivals].sort((a, b) => new Date(a) - new Date(b)).shift();
          this.props.setArrivalDate(closestArrival);
          this.fetchResults();
        }
      });
    window.addEventListener('scroll', this.throttled);
  }

  componentWillUnmount = () => {
    window.removeEventListener('scroll', this.throttled);
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.arrivalDate !== this.props.arrivalDate && !this.state.isFetching) {
      // Start fetching results if the arrival was changed due to another component (e.g. the price table)
      this.fetchResults();
    }
  }

  handlePreferenceToggle = () => this.setState(prevState => ({ stickyGalleryOpen: !prevState.stickyGalleryOpen }));

  scrollEventHandler = () => {
    const { stickyGallery } = this.state;
    if (window.innerWidth > 991) {
      if (stickyGallery && window.pageYOffset < 585) {
        this.setState({ stickyGallery: false });
        this.setState({ stickyGalleryOpen: false });
      }
      if (!stickyGallery && window.pageYOffset >= 585) {
        this.setState({ stickyGallery: true });
      }
    }
  }

  fetchResults = () => {
    this.setState({ isFetching: true });
    const focus = {
      resource: this.props.accommodation_type.id,
      allowRefetch: true,
      group_by: 'resource',
      sort: 'best_match',
      locale: trans('locale'),
    };

    const fetchOffersPromise = this.props.fetchOffers(focus)
      .then((data) => {
        if (!data) return this.setState({ isFetching: false });
        this.setState({ results: data.results, isFetching: false });
        return data;
      })
      .catch(() => {
        this.setState({ isFetching: false });
      });

    this.props.fetchArrivalsAndDepartures(focus);

    return fetchOffersPromise;
  }

  toggle = () => this.setState(prevState => ({
    popoverOpen: !prevState.popoverOpen,
  }));

  toggleAllInDesktop = () => this.setState(prevState => ({
    popoverOpenAllInDesktop: !prevState.popoverOpenAllInDesktop
  }));

  toggleAllInMobile = () => this.setState(prevState => ({
    popoverOpenAllInMobile: !prevState.popoverOpenAllInMobile
  }));

  getResult = () => {
    const { results } = this.state;
    const result = results && results.length && results[0];
    if (!result || !result.arrival_date) {
      return null;
    }

    return result;
  }

  render = () => {
    const { accommodation_type, subjectPreference, hasAdults } = this.props; // eslint-disable-line camelcase
    const { stickyGallery, stickyGalleryOpen } = this.state;
    const result = this.getResult();

    return (
      <div className={`gallery-holder ${stickyGallery ? 'affix' : 'affix-top'}`}>
        <div className="container">
          <div className="gallery-preferences">
            <header className="gallery-header">
              <h1>{accommodation_type.display_name || accommodation_type.name}</h1>
            </header>
            {accommodation_type.resort.id === 2041 && (
              <label className="label-srbb bg-red-light text-white font-weight-bold" style={{ zIndex: '999', whiteSpace: 'nowrap' }}>
                <i className="libema-icons">&#xE910;</i>
                {trans('resort.sr')}
              </label>
            )}
            {accommodation_type.resort.id !== 2041 && (
              <label className="label-vpbb bg-orange-light text-white font-weight-bold" style={{ zIndex: '999', whiteSpace: 'nowrap' }}>
                <i className="libema-icons">&#xE911;</i>
                {trans('resort.vp')}
              </label>
            )}
            {!this.state.error && (
              <div className={`gallery-body ${stickyGalleryOpen ? 'show' : ''}`}>
                <div className="row">
                  <div className="filters filters-accommodation" id="filters">
                    <div className="row">
                      <Filters
                        columnClass="col-12 pr-0"
                        petsAllowed={accommodation_type.max_pets > 0}
                        focus={this.props.focus}
                        results={this.state.results}
                        onChange={this.fetchResults}
                      />
                    </div>
                  </div>
                  {result && (
                    <div className="preference-data">
                      <div className="price-holder">
                        <div className="price-tag compact">
                          <span className="price">
                            {`${result.all_in_price},-`}
                          </span>
                          {(result.all_in_price < result.from_price) && (
                            <s>
                              {`${result.from_price},-`}
                            </s>
                          )}
                          {(result.special_name) && (
                            <p className="text-discount">{result.special_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="details-included details-included-desktop small my-3">
                        <div className="popover-included" id="popoverOpenAllInDesktop" onClick={this.toggleAllInDesktop}>
                          {trans('stay.including')}
                          {' '}
                          <span className="material-icons md-18">help</span>
                        </div>
                        <Popover className="popover popover-modal" placement="bottom" target="popoverOpenAllInDesktop" isOpen={this.state.popoverOpenAllInDesktop} toggle={this.toggleAllInDesktop}>
                          <div className="popover-content" dangerouslySetInnerHTML={{ __html: accommodation_type.all_in_device_description }} />
                          <div className="popover-close" onClick={this.toggleAllInDesktop}>
                            <i className="material-icons">&#xE5CD;</i>
                          </div>
                        </Popover>
                      </div>

                      <div className="details-included details-included-mobile small my-3">
                        <div className="popover-included" id="popoverOpenAllInMobile" onClick={this.toggleAllInDesktop}>
                          {trans('stay.including')}
                          {' '}
                          <span className="material-icons md-18">help</span>
                        </div>
                        <Popover className="popover popover-modal" placement="bottom" target="popoverOpenAllInMobile" isOpen={this.state.popoverOpenAllInMobile} toggle={this.toggleAllInMobile}>
                          <div className="popover-content" dangerouslySetInnerHTML={{ __html: accommodation_type.all_in_device_description }} />
                          <div className="popover-close" onClick={this.toggleAllInMobile}>
                            <i className="material-icons">&#xE5CD;</i>
                          </div>
                        </Popover>
                      </div>
                      <Link to={getBookingUrl(result, subjectPreference)} className={`btn btn-green btn-book ${hasAdults ? '' : 'disabled'}`}>
                        <span>
                          {trans('label.book_now')}
                        </span>
                        {' '}
                        <i className="material-icons">arrow_forward</i>
                      </Link>
                    </div>
                  )}
                  {!result && (
                    <div className="preference-data disabled">
                      <div className="price-holder">
                        <div className="price-tag compact">
                          {trans('label.not_available_in_period')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className={`preference-toggle ${stickyGalleryOpen ? 'preference-toggle-open' : ''}`}
                  onClick={this.handlePreferenceToggle}
                  tabIndex="0"
                  role="button"
                >
                  <span />
                </div>
              </div>
            )}
            {this.state.error && (
              <div className="alert alert-danger">{trans('error.newyse.general')}</div>
            )}
            <div className="gallery-footer p-t-0">
              <div className="inner">
                <div className="row">
                  <div className="col-md-6 pr-0">
                    <Link to={`${trans('routes.compare')}?resources[]=${this.props.accommodation_type.id}`} className="btn btn-link btn-block btn-compare">
                      {trans('label.compare_type')}
                    </Link>
                  </div>
                  {accommodation_type.resort.id === 2041 && (
                    <div className="col-md-6 pr-0">
                      <a
                        className="btn btn-link btn-eurosign hidden-sm-down"
                        tabIndex="0"
                        href="#price-table"
                      >
                        {trans('share.compare')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <ul className="gallery-nav list-unstyled list-inline">
            <li><a href={`#${trans('label.photos').toLowerCase()}`}>{trans('label.photos')}</a></li>
            <li><a href={`#${trans('label.description').toLowerCase()}`}>{trans('label.description')}</a></li>
            <li><a href={`#${trans('label.facilities').toLowerCase()}`}>{trans('label.facilities')}</a></li>
          </ul>
        </div>
      </div>
    );
  }
}

AccommodationInfoBlock.propTypes = {
  accommodation_type: PropTypes.object,
  arrivalDate: PropTypes.string,
  hasAdults: PropTypes.bool,
  fetchArrivalsAndDepartures: PropTypes.func,
  fetchOffers: PropTypes.func.isRequired,
  focus: PropTypes.object,
  setArrivalDate: PropTypes.func.isRequired,
  subjectPreference: PropTypes.object,
};

const mapStateToProps = state => ({
  arrivalDate: getPreference(PREFERENCE_ARRIVAL)(state),
  preferences: getPreferences(state),
  subjectPreference: getPreference(PREFERENCE_SUBJECTS)(state),
  hasAdults: hasAdults(state)
});

const mapDispatchToProps = dispatch => ({
  fetchOffers: focus => dispatch(fetchOffers(focus)),
  fetchArrivalsAndDepartures: focus => dispatch(fetchArrivalsAndDepartures(focus)),
  setArrivalDate: date => dispatch(setArrivalPreference(date))
});

export default connect(mapStateToProps, mapDispatchToProps)(AccommodationInfoBlock);
